import { defineStore } from 'pinia'
import { HttpStatusCode } from 'axios'
import { API } from '../../../services'
import type { InventoryTransferStatistics } from '../types'

const emptyStatistics = (): InventoryTransferStatistics => ({
  location_id: null,
  incoming_transfers: 0,
  outgoing_transfers: 0,
  pending_transfers: 0,
})

/** Bumps on each stats fetch so a stale response cannot overwrite a newer one. */
let statisticsFetchGeneration = 0

const toCount = (value: unknown): number => {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : 0
}

export const useTransferStatisticsStore = defineStore('inventoryTransferStatistics', {
  state: (): { statistics: InventoryTransferStatistics } => ({
    statistics: emptyStatistics(),
  }),

  getters: {
    incomingCount: (state) => toCount(state.statistics.incoming_transfers),
    outgoingCount: (state) => toCount(state.statistics.outgoing_transfers),
    pendingCount: (state) => toCount(state.statistics.pending_transfers),
  },

  actions: {
    reset() {
      statisticsFetchGeneration++
      this.statistics = emptyStatistics()
    },

    async dispatchGetTransferStatistics() {
      const generation = ++statisticsFetchGeneration
      try {
        const { status, data } = await API.transfers.getTransferStatistics()
        if (generation !== statisticsFetchGeneration) {
          return
        }
        if (status !== HttpStatusCode.Ok || !data?.data) {
          this.statistics = emptyStatistics()
          return
        }

        this.statistics = {
          location_id: data.data.location_id ?? null,
          incoming_transfers: toCount(data.data.incoming_transfers),
          outgoing_transfers: toCount(data.data.outgoing_transfers),
          pending_transfers: toCount(data.data.pending_transfers),
        }
      } catch {
        if (generation !== statisticsFetchGeneration) {
          return
        }
        this.statistics = emptyStatistics()
      }
    },
  },
})
